/**
 * Client-side CV export — PDF and Word from the generated CV data.
 *
 * PDF: opens a print-formatted window; the browser's print dialog ("Save as
 * PDF") produces a clean A4 document. No server or LaTeX toolchain needed.
 * Word: builds a real .docx with the `docx` library and downloads it.
 *
 * Both render the same ATS-safe single-column layout as the LaTeX template:
 * name + contact header, Summary, Experience, Education, Skills, Languages.
 */
import {
  BorderStyle,
  Document,
  Packer,
  Paragraph,
  TabStopType,
  TextRun,
} from 'docx';

export interface TailoredCv {
  tailored_summary: string;
  skills_focus: string[];
  experience: { role: string; company: string; dates: string; location: string; bullets: string[] }[];
  // Set by the backend anonymity pass; controls how the header/education render.
  _anon?: { level: number; name?: string; hideContact?: boolean };
}

export interface CvProfile {
  full_name?: string;
  email?: string;
  phone?: string;
  location?: string;
  linkedin_url?: string;
  education?: { degree?: string; institution?: string; year?: string }[];
  skills?: { languages?: string[] };
}

function contactLine(profile: CvProfile, anon?: TailoredCv['_anon']): string {
  if (anon?.hideContact) return '';
  return [profile.email, profile.phone, profile.location, profile.linkedin_url]
    .filter(Boolean)
    .join('  |  ');
}

function displayName(profile: CvProfile, anon?: TailoredCv['_anon']): string {
  return anon && anon.level >= 35 && anon.name ? anon.name : (profile.full_name ?? '');
}

function fileBase(profile: CvProfile, company?: string): string {
  const raw = ['CV', profile.full_name, company].filter(Boolean).join(' - ');
  return raw.replace(/[<>:"/\\|?*]+/g, '').trim() || 'CV';
}

// ── PDF (print window) ───────────────────────────────────────────────────────

function escHtml(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function cvHtml(profile: CvProfile, tailored: TailoredCv, title: string): string {
  const exp = (tailored.experience ?? [])
    .map(
      (r) => `
      <div class="job">
        <div class="row"><span class="role">${escHtml(r.role)}</span><span class="dates">${escHtml(r.dates)}</span></div>
        <div class="row sub"><span>${escHtml(r.company)}</span><span>${escHtml(r.location)}</span></div>
        <ul>${(r.bullets ?? []).map((b) => `<li>${escHtml(b)}</li>`).join('')}</ul>
      </div>`
    )
    .join('');

  const anon = tailored._anon;
  const edu = (profile.education ?? [])
    .map((e) => {
      const inst = anon && anon.level >= 75 ? 'a leading institution' : escHtml(e.institution);
      const year = anon && anon.level >= 90 ? '' : escHtml(e.year);
      return `
      <div class="row edu"><span><strong>${escHtml(e.degree)}</strong>, ${inst}</span><span class="dates">${year}</span></div>`;
    })
    .join('');

  const langs = (profile.skills?.languages ?? []).join(', ');

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${escHtml(title)}</title>
<style>
  @page { size: A4; margin: 16mm 15mm; }
  * { box-sizing: border-box; }
  body { font-family: Calibri, 'Segoe UI', Arial, sans-serif; color: #1a1a1a;
         font-size: 10.5pt; line-height: 1.35; margin: 0; }
  @media screen { body { padding: 28px; background: #fff; } }
  h1 { font-size: 20pt; margin: 0; letter-spacing: 0.5px; }
  .contact { font-size: 9.5pt; color: #444; margin: 2pt 0 10pt; }
  h2 { font-size: 11pt; text-transform: uppercase; letter-spacing: 1px;
       border-bottom: 1px solid #999; padding-bottom: 2pt; margin: 12pt 0 6pt; }
  .row { display: flex; justify-content: space-between; gap: 12pt; }
  .role { font-weight: bold; }
  .sub { font-style: italic; font-size: 9.5pt; color: #444; }
  .dates { color: #444; font-size: 9.5pt; white-space: nowrap; }
  ul { margin: 3pt 0 8pt; padding-left: 16pt; }
  li { margin: 1.5pt 0; }
  .job { page-break-inside: avoid; margin-bottom: 4pt; }
  .edu { margin: 2pt 0; }
  p { margin: 0; }
</style></head>
<body>
  <h1>${escHtml(displayName(profile, anon))}</h1>
  <div class="contact">${escHtml(contactLine(profile, anon))}</div>
  <h2>Summary</h2>
  <p>${escHtml(tailored.tailored_summary)}</p>
  <h2>Experience</h2>
  ${exp}
  ${edu ? `<h2>Education</h2>${edu}` : ''}
  <h2>Skills</h2>
  <p>${escHtml((tailored.skills_focus ?? []).join(', '))}</p>
  ${langs ? `<h2>Languages</h2><p>${escHtml(langs)}</p>` : ''}
</body></html>`;
}

/** The document-style HTML view of the CV — used for inline preview (iframe)
 *  and as the print body for Save-as-PDF. */
export function cvPreviewHtml(profile: CvProfile, tailored: TailoredCv, company?: string): string {
  return cvHtml(profile, tailored, fileBase(profile, company));
}

/** Opens the print dialog on a formatted CV — "Save as PDF" downloads it. */
export function exportCvPdf(profile: CvProfile, tailored: TailoredCv, company?: string): void {
  const title = fileBase(profile, company); // becomes the suggested PDF filename
  const w = window.open('', '_blank');
  if (!w) throw new Error('Popup blocked — allow popups for this site to export PDF.');
  w.document.write(cvHtml(profile, tailored, title));
  w.document.close();
  // Give the new window a moment to layout before printing.
  setTimeout(() => { w.focus(); w.print(); }, 300);
}

// ── Word (.docx) ─────────────────────────────────────────────────────────────

const RIGHT_TAB = [{ type: TabStopType.RIGHT, position: 10440 }]; // ~page width at default margins

function sectionHeading(text: string): Paragraph {
  return new Paragraph({
    spacing: { before: 220, after: 80 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: '999999' } },
    children: [new TextRun({ text: text.toUpperCase(), bold: true, size: 22 })],
  });
}

export async function exportCvDocx(profile: CvProfile, tailored: TailoredCv, company?: string): Promise<void> {
  const anon = tailored._anon;
  const children: Paragraph[] = [
    new Paragraph({
      children: [new TextRun({ text: displayName(profile, anon), bold: true, size: 40 })],
    }),
    new Paragraph({
      spacing: { after: 160 },
      children: [new TextRun({ text: contactLine(profile, anon), size: 19, color: '444444' })],
    }),
    sectionHeading('Summary'),
    new Paragraph({ children: [new TextRun({ text: tailored.tailored_summary ?? '', size: 21 })] }),
    sectionHeading('Experience'),
  ];

  for (const r of tailored.experience ?? []) {
    children.push(
      new Paragraph({
        tabStops: RIGHT_TAB,
        spacing: { before: 120 },
        children: [
          new TextRun({ text: r.role ?? '', bold: true, size: 21 }),
          new TextRun({ text: `\t${r.dates ?? ''}`, size: 19, color: '444444' }),
        ],
      }),
      new Paragraph({
        tabStops: RIGHT_TAB,
        children: [
          new TextRun({ text: r.company ?? '', italics: true, size: 19, color: '444444' }),
          new TextRun({ text: `\t${r.location ?? ''}`, italics: true, size: 19, color: '444444' }),
        ],
      })
    );
    for (const b of r.bullets ?? []) {
      children.push(
        new Paragraph({ bullet: { level: 0 }, children: [new TextRun({ text: b, size: 20 })] })
      );
    }
  }

  const edu = profile.education ?? [];
  if (edu.length) {
    children.push(sectionHeading('Education'));
    for (const e of edu) {
      const inst = anon && anon.level >= 75 ? 'a leading institution' : e.institution;
      const year = anon && anon.level >= 90 ? '' : (e.year ?? '');
      children.push(
        new Paragraph({
          tabStops: RIGHT_TAB,
          children: [
            new TextRun({ text: e.degree ?? '', bold: true, size: 20 }),
            new TextRun({ text: inst ? `, ${inst}` : '', size: 20 }),
            new TextRun({ text: `\t${year}`, size: 19, color: '444444' }),
          ],
        })
      );
    }
  }

  children.push(
    sectionHeading('Skills'),
    new Paragraph({ children: [new TextRun({ text: (tailored.skills_focus ?? []).join(', '), size: 20 })] })
  );

  const langs = profile.skills?.languages ?? [];
  if (langs.length) {
    children.push(
      sectionHeading('Languages'),
      new Paragraph({ children: [new TextRun({ text: langs.join(', '), size: 20 })] })
    );
  }

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileBase(profile, company)}.docx`;
  a.click();
  URL.revokeObjectURL(url);
}
